import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, Modal, Select, Space, TimePicker, Typography } from 'antd';
import moment from 'moment';
import React from 'react';
import { PricingAPI } from '../../../../../controller/PricingAPI';
import { departmentOptionsFor, showRequestError } from '../todoHelpers';

// 對應後端 TodoTemplate::CHECKLIST_MAX；兩邊要一起改，否則超過時使用者會拿到英文的驗證訊息
const CHECKLIST_MAX = 50;

// 新增與編輯共用。系統待辦 (source !== 'manual') 的主旨與描述是排程產生的，只開放改負責人、部門、到期日與排程。
// 被指派但不能編輯的人 (can.update 為 false) 只會看到「設定排程」：開始日期與開始時間
function TodoFormModal({ open, todo, onClose, onSaved }) {
    const [form] = Form.useForm();
    const [userOptions, setUserOptions] = React.useState([]);
    const [departmentOptions, setDepartmentOptions] = React.useState([]);
    const [saving, setSaving] = React.useState(false);
    const isEdit = !!todo;
    const isSystem = isEdit && todo.source !== 'manual';
    const scheduleOnly = isEdit && !!todo.can && todo.can.update === false;
    // agenda 的列不內嵌 description (只給 has_description)，編輯前要先取回來；
    // 沒取到就不能把空值當成「清空描述」送出去
    const needsDescription = isEdit && !isSystem && !scheduleOnly
        && !Object.prototype.hasOwnProperty.call(todo, 'description') && todo.has_description === true;
    const [descriptionState, setDescriptionState] = React.useState('idle');
    const descriptionLoading = needsDescription && descriptionState === 'loading';
    const descriptionLoaded = !needsDescription || descriptionState === 'done';

    React.useEffect(() => {
        if (!open || !needsDescription) {
            return undefined;
        }
        let active = true;
        setDescriptionState('loading');
        PricingAPI.todos.show(todo.id).then((response) => {
            if (!active) {
                return;
            }
            form.setFieldsValue({ description: response.data.description || '' });
            setDescriptionState('done');
        }).catch((error) => {
            if (active) {
                setDescriptionState('failed');
                showRequestError(error);
            }
        });
        return () => {
            active = false;
        };
    }, [open, needsDescription, todo, form]);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        PricingAPI.users.options().then((response) => {
            setUserOptions(response.data.map((user) => ({ value: user.id, label: user.name })));
        }).catch(showRequestError);
        PricingAPI.departments.options().then((response) => {
            setDepartmentOptions(response.data);
        }).catch(showRequestError);
        const loginUser = PricingAPI.auth.getLoginUser();
        form.setFieldsValue(isEdit ? {
            subject: todo.subject,
            description: isSystem ? '' : todo.description,
            assignee_id: todo.assignee_id,
            department_id: todo.department_id,
            due_date: todo.due_date ? moment(todo.due_date, 'YYYY-MM-DD') : null,
            start_date: todo.start_date ? moment(todo.start_date, 'YYYY-MM-DD') : null,
            start_time: todo.start_time ? moment(todo.start_time, 'HH:mm:ss') : null,
            // 回填來源是 items 的 label；todo.checklist 是 {done, total} 的計數物件，不是陣列
            checklist: todo.items ? todo.items.map((i) => i.label) : [],
        } : {
            subject: '',
            description: '',
            assignee_id: loginUser ? loginUser.id : undefined,
            department_id: undefined,
            due_date: null,
            start_date: null,
            start_time: null,
            checklist: [],
        });
    }, [open, todo, isEdit, isSystem, form]);

    const handleOk = () => {
        form.validateFields().then((values) => {
            const schedule = {
                start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
                start_time: values.start_time ? values.start_time.format('HH:mm') : null,
            };
            const payload = scheduleOnly ? schedule : {
                assignee_id: values.assignee_id ?? null,
                department_id: values.department_id ?? null,
                due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
                ...schedule,
            };
            if (!scheduleOnly && !isSystem) {
                payload.subject = values.subject;
                if (descriptionLoaded) {
                    payload.description = values.description || null;
                }
                payload.checklist = (values.checklist || []).filter((label) => (label || '').trim() !== '');
            }
            setSaving(true);
            const request = isEdit ? PricingAPI.todos.update(todo.id, payload) : PricingAPI.todos.store(payload);
            request.then(() => {
                onSaved();
            }).catch(showRequestError).finally(() => {
                setSaving(false);
            });
        });
    };

    return (
        <Modal
            title={scheduleOnly ? '設定排程' : isEdit ? '編輯待辦' : '新增待辦'}
            visible={open}
            onOk={handleOk}
            onCancel={onClose}
            confirmLoading={saving}
            okButtonProps={{ disabled: descriptionLoading }}
            okText="送出"
            cancelText="取消"
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                {!scheduleOnly && (<>
                <Form.Item name="subject" label="主旨" rules={[{ required: true, message: '請輸入主旨' }, { max: 200, message: '主旨最多 200 字' }]}>
                    <Input disabled={isSystem} />
                </Form.Item>
                {isSystem ? (
                    <Typography.Paragraph type="secondary">系統產生的待辦只能修改負責人、部門與排程；主旨與描述由產生它的排程或例行範本維護。</Typography.Paragraph>
                ) : (
                    <Form.Item name="description" label="描述">
                        <Input.TextArea rows={5} disabled={descriptionLoading} placeholder={descriptionLoading ? '載入描述中...' : undefined} />
                    </Form.Item>
                )}
                <Form.Item name="assignee_id" label="負責人">
                    <Select
                        showSearch
                        allowClear
                        placeholder="未指派"
                        options={userOptions}
                        optionFilterProp="label"
                    />
                </Form.Item>
                <Form.Item name="department_id" label="部門" extra="只填部門不填負責人時，該部門成員可以自行認領">
                    <Select
                        showSearch
                        allowClear
                        placeholder="未指定"
                        options={departmentOptionsFor(departmentOptions, todo)}
                        optionFilterProp="label"
                    />
                </Form.Item>
                {!isSystem && (
                    <Form.Item label="檢查清單" extra={`處理時只能打勾，內容在這裡設定；留空代表沒有清單；最多 ${CHECKLIST_MAX} 項`}>
                        <Form.List name="checklist">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, ...restField }) => (
                                        <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 4 }}>
                                            <Form.Item {...restField} noStyle>
                                                <Input maxLength={200} style={{ width: 320 }} placeholder="這一項要做什麼" />
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(restField.name)} />
                                        </Space>
                                    ))}
                                    <Button type="dashed" size="small" disabled={fields.length >= CHECKLIST_MAX} onClick={() => add('')} icon={<PlusOutlined />}>
                                        新增一項
                                    </Button>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>
                )}
                </>)}
                <Form.Item name="start_date" label="開始日期" extra="預計哪一天做；清空會一併清掉開始時間">
                    <DatePicker style={{ width: '100%' }} onChange={(d) => { if (!d) { form.setFieldsValue({ start_time: null }); } }} />
                </Form.Item>
                <Form.Item shouldUpdate={(prev, cur) => prev.start_date !== cur.start_date} noStyle>
                    {({ getFieldValue }) => (
                        <Form.Item name="start_time" label="開始時間" extra={getFieldValue('start_date') ? undefined : '請先選開始日期'}>
                            <TimePicker style={{ width: '100%' }} format="HH:mm" minuteStep={5} disabled={!getFieldValue('start_date')} />
                        </Form.Item>
                    )}
                </Form.Item>
                {!scheduleOnly && (
                    <Form.Item name="due_date" label="到期日">
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
}

export default TodoFormModal;
